/**
 * learningModel — reglas puras de los capítulos de aprendizaje (sin React),
 * para poder testearlas sin montar nada.
 */

export const SESSION_KEY = 'sml.learning.session';

// Eventos globales (la barra, el recorrido y el gate hablan por acá).
export const EVT_OPEN = 'sml:learning-open';      // detail: { chapterId? }
export const EVT_CLOSED = 'sml:learning-closed';  // detail: { mode }
export const EVT_FEEDBACK = 'sml:feedback-open';  // detail: { kind: 'problem'|'idea' }
export const EVT_DATA = 'sml:learning-data';      // detail: datos de /api/learning

export const openLearning = (chapterId) => window.dispatchEvent(
  new CustomEvent(EVT_OPEN, { detail: { chapterId } }));
export const openFeedback = (kind) => window.dispatchEvent(
  new CustomEvent(EVT_FEEDBACK, { detail: { kind } }));

/** «1:16», «12 s», «—». */
export const formatDuration = (seconds) => {
  const s = Math.round(Number(seconds) || 0);
  if (s <= 0) return '—';
  if (s < 60) return `${s} s`;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export const isPlayable = (chapter) => chapter?.status === 'published' && !!chapter?.video_url;

export const youtubeVideoId = (value = '') => {
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.username || url.password || url.port) return null;
    const host = url.hostname.toLowerCase().replace(/\.$/, '');
    const parts = url.pathname.split('/').filter(Boolean);
    let id = null;
    if (host === 'youtu.be' && parts.length === 1) id = parts[0];
    if (['youtube.com', 'www.youtube.com', 'm.youtube.com', 'music.youtube.com'].includes(host)) {
      if (url.pathname === '/watch') id = url.searchParams.get('v');
      if (parts.length === 2 && ['embed', 'shorts'].includes(parts[0])) id = parts[1];
    }
    return /^[A-Za-z0-9_-]{11}$/.test(id || '') ? id : null;
  } catch { return null; }
};

export const youtubeEmbedUrl = (value) => {
  const id = youtubeVideoId(value);
  return id ? `https://www.youtube.com/embed/${id}` : null;
};

export const youtubeThumbnailCandidates = (value) => {
  const id = youtubeVideoId(value);
  if (!id) return [];
  return [
    `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`,
    `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
  ];
};

export const chapterThumbnailCandidates = (chapter) => {
  if (chapter?.thumb_url) return [chapter.thumb_url];
  if (chapter?.video_source !== 'youtube') return [];
  return youtubeThumbnailCandidates(chapter.video_url);
};

export const sortChapters = (chapters = []) => [...chapters].sort(
  (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || (a.number ?? 0) - (b.number ?? 0));

/** El que se reproduce primero: el destacado; si no, el primero publicado. */
export const pickFeatured = (chapters = []) => {
  const playable = sortChapters(chapters).filter(isPlayable);
  return playable.find((c) => c.featured) || playable[0] || null;
};

/** ¿Abrimos los capítulos al iniciar? */
export const shouldShowOnboarding = (data, { sessionShown = false } = {}) => {
  if (!data || sessionShown) return false;
  const ob = data.onboarding || {};
  if (!ob.enabled || ob.hidden) return false;
  return (data.chapters || []).some((c) => ob.chapter_ids?.includes(c.id) && isPlayable(c));
};

export const onboardingChapters = (data) => {
  const ids = data?.onboarding?.chapter_ids || [];
  return sortChapters((data?.chapters || []).filter((c) => ids.includes(c.id)));
};

export const hasUnseen = (data) => {
  const seen = new Set(data?.state?.seen || []);
  return (data?.chapters || []).some((c) => isPlayable(c) && !seen.has(c.id));
};

export const chapterLabel = (chapter) => `Ep. ${String(chapter?.number ?? 0).padStart(2, '0')}`;

export const topicsOf = (chapters = []) => {
  const out = [];
  sortChapters(chapters).forEach((c) => {
    if (c.topic && !out.includes(c.topic)) out.push(c.topic);
  });
  return out;
};

export const filterByTopic = (chapters = [], topic) => (
  !topic || topic === 'Todos' ? sortChapters(chapters)
    : sortChapters(chapters).filter((c) => c.topic === topic));

/** Contexto que se adjunta a un reporte: nada personal ni secreto. */
export const feedbackContext = ({ projectId, projectName, view } = {}) => {
  const ctx = {};
  try {
    ctx.route = window.location.pathname;
    ctx.user_agent = navigator.userAgent;
    ctx.viewport = `${window.innerWidth}x${window.innerHeight}`;
    ctx.language = navigator.language;
    ctx.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch { /* entorno sin navegador */ }
  if (projectId) ctx.project_id = projectId;
  if (projectName) ctx.project_name = projectName;
  if (view) ctx.view = view;
  return ctx;
};
